<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { post } from '@/api/Services'
import { useStore } from 'vuex'

const store = useStore()
const loggeduser = computed(() => store.state.account.loggeduser)

const cmsData = ref([])
const faq = ref([]) // Filtrelenen FAQ
const originalFaq = ref([]) // Orijinal FAQ
const faqGroupRef = ref([]) // Gruplar

const activeIndex = ref(null) // Aktif Accordion grubu
const accordionActiveIndex = ref(null) // Accordion için ayrı bir index ekle
const loading = ref(true) // Yükleme durumu

// Accordion tıklama işlemi
const handleAccordionClick = (event) => {
  accordionActiveIndex.value = event.index
}

// İçindekiler tablosundan bir gruba tıklama
const handleContentClick = (index) => {
  activeIndex.value = index
  accordionActiveIndex.value = null // Grup değiştiğinde accordion'u kapat
}

// FAQ'yi activeIndex'e göre filtreleme
watch(activeIndex, async (newIndex) => {
  if (newIndex) {
    await fetchFaq(newIndex)
    accordionActiveIndex.value = null // Sorular değiştiğinde accordion'u kapat
  }
})

// CMS verilerini çek
const cms = async () => {
  const parameters = [
    {
      parameter_name: 'pin_user_id',
      parameter_value: loggeduser.value ? loggeduser.value.id : -1,
    },
    {
      parameter_name: 'pin_page_id',
      parameter_value: 9,
    },
  ]

  await post('/Commons/Executer', {
    sqlId: 76,
    parameters: JSON.stringify(parameters),
  })
    .then((data) => {
      cmsData.value = data
    })
    .catch((err) => console.log(err))
}

// FAQ gruplarını çek
const faqGroup = async () => {
  const parameters = [
    {
      parameter_name: 'pin_user_id',
      parameter_value: -1,
    },
  ]

  await post('/Commons/Executer', {
    sqlId: 77,
    parameters: JSON.stringify(parameters),
  })
    .then((data) => {
      // order_id'ye göre sırala
      const sortedData = data.sort((a, b) => a.order_id - b.order_id)
      faqGroupRef.value = sortedData
      // General grubu varsa onun ID'sini al
      handleContentClick(sortedData[0].id)
    })
    .catch((err) => console.log(err))
}

// FAQ verilerini çek
const fetchFaq = async (groupId = -1) => {
  const parameters = [
    {
      parameter_name: 'pin_user_id',
      parameter_value: -1,
    },
    {
      parameter_name: 'pin_cms_faq_group_id',
      parameter_value: groupId,
    },
  ]

  await post('/Commons/Executer', {
    sqlId: 78,
    parameters: JSON.stringify(parameters),
  })
    .then((data) => {
      // order_id'ye göre sırala
      const sortedData = data.sort((a, b) => a.order_id - b.order_id)
      faq.value = sortedData
      originalFaq.value = [...sortedData]
    })
    .catch((err) => console.log(err))
}

// Sayfa yüklendiğinde verileri çek
onMounted(async () => {
  loading.value = true
  await cms()
  await faqGroup() // Önce grupları çek ve General'ı bul
  // activeIndex watch'ı tetiklenecek ve ilgili soruları çekecek

  loading.value = false
})
</script>

<template>
  <Skeleton width="1140px" height="500px" class="mx-auto" v-if="loading" />
  <div class="container mt-5 p-0" v-else>
    <!-- Başlık ve Alt Başlık -->
    <div
      class="d-flex align-content-center justify-content-center flex-column text-center"
    >
      <h1 class="faq-title">{{ cmsData[0]?.header }}</h1>
      <p class="faq-subtitle">
        {{ cmsData[0]?.header_subtext }}
      </p>
    </div>

    <div class="d-grid grid-columns-3 mt-5 gap-10">
      <!-- İçindekiler Tablosu -->
      <div class="grid-col-span-1">
        <div class="d-flex flex-column justify-content-center gap-6">
          <h2 class="table-of-content-title">Table of Content</h2>
          <ul class="faq-list">
            <li v-for="item in faqGroupRef" :key="item.id">
              <!-- Aktif olan sekmeye `active` sınıfı ekle ve tıklandığında `handleContentClick` fonksiyonunu çağır -->
              <div
                :class="{ active: activeIndex === item.id }"
                class="table-of-content pointer"
                @click="handleContentClick(item.id)"
              >
                {{ item.definition }}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- SSS İçeriği -->
      <div class="grid-col-span-2">
        <Accordion
          :activeIndex="accordionActiveIndex"
          expandIcon="pi pi-plus"
          collapseIcon="pi pi-minus"
          @tab-open="handleAccordionClick"
        >
          <AccordionTab
            v-for="(item, index) in faq"
            :header="item.question"
            :key="item.id"
            :id="item.id"
            @click="handleAccordionClick(index)"
          >
            <p class="m-0 faq-accordion-content">{{ item.answer }}</p>
          </AccordionTab>
        </Accordion>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@media (max-width: 1024px) {
  .grid-columns-3 {
    grid-template-columns: 1fr;
  }
  .faq-title {
    font-size: 32px !important;
  }
  .table-of-content-title {
    display: none;
  }
  .faq-list {
    display: flex;
    gap: 20px;
    margin-bottom: 0;
    border-bottom: 2px solid #e9e9e9;
    overflow: auto;
    padding-bottom: 40px !important;
    li {
      flex-shrink: 0;
      margin-bottom: 0 !important;
    }
    .table-of-content {
      &.active {
        position: relative;
        &::after {
          position: absolute;
          bottom: -2px;
          width: 100% !important;
        }
      }
    }
  }
}
.table-of-content-title {
  font-size: 24px;
  color: #000;
  font-weight: 700;
}
.faq-list {
  list-style: none;
  padding: 0;
  li {
    margin-bottom: 24px;
    .table-of-content {
      font-weight: 500;
      font-size: 16px;
      color: #989898;
      letter-spacing: 0.01em;
      display: flex;
      align-items: center;
      gap: 10px;
      &.active {
        color: #fc6c2a;
        &::after {
          content: '';
          width: 20%;
          height: 2px;
          background-color: #fc6c2a;
        }
      }
    }
  }
}
::v-deep(.p-accordion-tab) {
  box-shadow: unset !important;
  border-bottom: 1px solid #e9e9e9;
  a {
    text-decoration: none !important;
    padding: 0 !important;
    margin-block: 16px !important;
    background-color: none !important;
  }
  .p-accordion-toggle-icon {
    order: unset !important;
    margin-left: unset !important;
    margin-right: 12px !important;
  }
  .p-accordion-toggle-icon {
    color: #fc6c2a;
  }
  .p-accordion-header-text {
    font-size: 18px;
    font-weight: 500;
    color: #2c2c2c;
  }
  .faq-accordion-content {
    font-size: 16px;
    font-weight: 500;
    color: #2c2c2c;
    padding: 0 !important;
  }
}
.faq-container {
  max-width: 1140px;
  width: 100%;
  margin: auto;
  padding-inline: 20px;
}
.faq-title {
  font-size: 50px;
  font-weight: 700;
  color: #444444;
}
.faq-subtitle {
  font-size: 16px;
  font-weight: 400;
  color: #2c2c2c;
}
</style>
